import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Loader2, ScanLine, Download, AlertCircle,
  Factory, Warehouse,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const moduleStyle = (module) => {
  if (module === 'Greige Section') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
  if (module === 'Greige Storage') return 'bg-teal-100 text-teal-700 border-teal-200';
  return 'bg-gray-100 text-gray-500 border-gray-200';
};

const statusStyle = (rank) => {
  if (rank === 0) return 'bg-gray-100 text-gray-500';
  if (rank === 10) return 'bg-emerald-100 text-emerald-700';
  if (rank >= 8) return 'bg-teal-50 text-teal-700';
  return 'bg-indigo-50 text-indigo-700';
};

const DocTrack = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (q.length < 3) {
      toast.warning('Enter a document barcode (min 3 characters)');
      return;
    }
    setLoading(true);
    setData(null);
    setNotFound(false);
    try {
      const res = await apiClient.get('/greige-storage/doc-track', {
        params: { document_barcode: q },
      });
      if (!res.data?.found || (res.data?.total_bundles ?? 0) === 0) {
        setNotFound(true);
        setData(res.data?.found ? res.data : null);
      } else {
        setData(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load doc track report');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!data?.bundles?.length) return;
    const header = ['Bundle Number', 'Module', 'Operation Status'];
    const lines = data.bundles.map((b) =>
      [b.bundle_barcode, b.module, b.operation_status]
        .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `doc_track_${(data.document_barcode || 'report').replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const rows = data?.bundles || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige-storage')}
            data-testid="doc-track-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-sky-600 text-white"><ScanLine className="w-5 h-5" /></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Doc Track Report</h1>
              <p className="text-sm text-gray-500">Greige · Enter a document barcode to see each bundle's status</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Scan or type document barcode (e.g. DOC016413/25-26)"
            className="text-base"
            data-testid="doc-track-input"
          />
          <Button type="submit" disabled={loading} data-testid="doc-track-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2 hidden sm:inline">Track</span>
          </Button>
        </form>

        {notFound && (
          <Card data-testid="doc-track-notfound">
            <CardContent className="py-10 text-center">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No bundles found for “{query.trim()}”</p>
              <p className="text-sm text-gray-500 mt-1">Check the document barcode and try again.</p>
            </CardContent>
          </Card>
        )}

        {data && rows.length > 0 && (
          <div data-testid="doc-track-result">
            {/* Summary */}
            <Card className="mb-5 border-sky-200">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Document</p>
                      <p className="text-lg font-bold text-gray-900" data-testid="doc-track-doc-code">{data.document_barcode}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Vendor</p>
                      <p className="font-medium text-gray-800">{data.vendor_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Section</p>
                      <p className="font-medium text-gray-800">{(data.section_names || []).join(', ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Bundles</p>
                      <p className="font-semibold text-sky-700" data-testid="doc-track-total">{data.total_bundles}</p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={exportCsv} data-testid="doc-track-export-btn">
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>

                {/* stage summary chips */}
                {data.summary && Object.keys(data.summary).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                    {Object.entries(data.summary).map(([label, count]) => (
                      <Badge key={label} variant="secondary" className="text-xs font-normal"
                        data-testid={`doc-track-summary-${label.replace(/\s+/g, '-').toLowerCase()}`}>
                        {label}: <span className="font-semibold ml-1">{count}</span>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardContent className="p-0">
                <Table data-testid="doc-track-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Bundle Number</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Operation Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((b, idx) => (
                      <TableRow key={b.bundle_barcode} data-testid={`doc-track-row-${idx}`}>
                        <TableCell className="text-gray-400 text-sm">{idx + 1}</TableCell>
                        <TableCell className="font-medium text-gray-900" data-testid={`doc-track-bundle-${idx}`}>
                          {b.bundle_barcode}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${moduleStyle(b.module)}`}
                            data-testid={`doc-track-module-${idx}`}>
                            {b.module === 'Greige Section' && <Factory className="w-3 h-3" />}
                            {b.module === 'Greige Storage' && <Warehouse className="w-3 h-3" />}
                            {b.module}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${statusStyle(b.rank)}`}
                            data-testid={`doc-track-status-${idx}`}>
                            {b.operation_status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocTrack;
